import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Modal,
  TextField
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DataGrid } from '@material-ui/data-grid';

import { setNewSOProducts, setNewSOPaymentInfo, refundSalesOrder } from 'redux/actions';
import { VAT_UAE } from 'variables/globalVAT';

const mapStateToProps = (state) => ({
  newSalesOrder: state.newSalesOrder
});

const RefundProductsModal = (props) => {

  const { displayId, customerId, soProducts, total, paymentMethod } = props.newSalesOrder;

  //this is a unique case where the `id` used for DataGrid is the
  //database id given to the SalesOrderProduct.
  const [updatedProducts, setUpdatedProducts] = useState(
    soProducts.map(soProduct => ({
      ...soProduct,
      firstLetter: soProduct.name[0].toUpperCase(),
      refundQuantity: 0
    }))
  );

  const [productInput, setProductInput] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");

  const visibleProducts = updatedProducts.filter(product => product.quantity > 0);

  const columns = [
    { field: 'displayId', headerName: 'Product ID', flex: 1 },
    { field: 'name', headerName: 'Product Name', flex: 1 },
    { field: 'company', headerName: 'Brand', flex: 1 },
    { field: 'unitPrice', headerName: 'Unit Price', type: 'number', flex: 1 },
    { field: 'quantity', headerName: 'Quantity', type: 'number', flex: 1 }
  ];

  const reduceProductQuantity = (productId) => {
    const index = updatedProducts.findIndex(updatedProduct => updatedProduct.productId === productId);
    //if the product is in the salesOrder and its quantity is atleast one, reduce its quantity.
    if( index > -1 && updatedProducts[index].quantity > 0 ) {
      setUpdatedProducts([
        ...updatedProducts.slice(0, index),
        {
          ...updatedProducts[index],
          refundQuantity: updatedProducts[index].refundQuantity + 1,
          quantity: updatedProducts[index].quantity - 1
        },
        ...updatedProducts.slice(index + 1)
      ]);
    }
  };

  const checkProductBarcode = (e) => {
    setBarcodeInput(e.target.value);

    if(e.target.value.length > 3) {
      const productInfo = props.products.find(product => product.barcode === e.target.value);
      if(productInfo) {
        reduceProductQuantity(productInfo.id);
        setBarcodeInput("");
      }
    }
  };

  const saveRefundProducts = () => {
    //This is a temporary method to get the salesOrder id. improve it. also SalesOrderTable already
    //gets the salesOrderInfo using the find method, so here we are doing it twice.
    const salesOrderInfo = props.salesOrders.find(salesOrder => salesOrder.displayId === displayId && salesOrder.total === total);

    let refundProducts = [];
    let incrementProducts = [];
    for ( let i = 0; i < updatedProducts.length; i++ ) {
      const updatedProduct = updatedProducts[i];
      if(updatedProduct.refundQuantity > 0) {
        refundProducts.push({
          id: updatedProduct.id,
          quantity: updatedProduct.quantity,
          refundQuantity: updatedProduct.refundQuantity
        });
        incrementProducts.push({
          id: updatedProduct.productId,
          incrementQuantity: updatedProduct.refundQuantity
        });
      }
    }

    //atleast one product has to be refunded to initiate a salesOrder Refund.
    if(refundProducts.length > 0) {
      const subTotal = updatedProducts.reduce((prev, curr) => prev + ( curr.unitPrice * curr.quantity ), 0);
      const totalVat = Math.round( subTotal * VAT_UAE );
      const grandTotal = subTotal + totalVat;

      const refundedSalesOrder = {
        id: salesOrderInfo.id,
        refundProducts,
        updates: {
          totalVat,
          total: grandTotal
        }
      };
      const updates = {
        updatedProducts: incrementProducts,
        paymentMethod,
        customerId,
        decrementAmount: total - grandTotal
      };
      props.refundSalesOrder(refundedSalesOrder, updates, props.user.adminId);

      props.setNewSOProducts(visibleProducts.map(({ firstLetter, refundQuantity, ...visibleProduct }) => visibleProduct));
      props.setNewSOPaymentInfo({ totalVat, total: grandTotal, paymentMethod });

      props.closeModal(true);
    }
  };

  return (
    <Modal open={props.open}>
      <Card>
        <CardHeader
          subheader="Please Select the Products to Refund."
          title="Refund Products"
        />
        <Divider />
        <CardContent>
          <Box>
            <Grid
              container
              spacing={3}
              style={{
                marginTop: '0.3%'
              }}
            >
              <Grid
                item
                md={6}
                xs={12}
              >
                <Autocomplete
                  onChange={(event, newValue) => {
                    reduceProductQuantity(newValue.productId);
                    setProductInput("");
                  }}
                  id="free-solo-dialog-demo"
                  fullWidth
                  options={visibleProducts.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
                  groupBy={(product) => product.firstLetter}
                  getOptionSelected={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (<TextField {...params} label="Select Product.." variant="outlined" />)}
                  inputValue={productInput}
                  onInputChange={(event, newInputValue) => {
                    setProductInput(newInputValue);
                  }}
                  value={null}
                />
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
              >
                <TextField
                  fullWidth
                  helperText="Scan Product Barcode to Remove Quantity"
                  label="Scan Barcode to Refund"
                  name="barcode"
                  onChange={(event) => checkProductBarcode(event)}
                  value={barcodeInput}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <Box
              style={{
                overflow: 'auto',
                height: 266,
                marginTop: '1%'
              }}
            >
              <DataGrid
                rows={visibleProducts}
                columns={columns}
                disableSelectionOnClick
                hideFooter
                localeText={{
                  noRowsLabel: 'All products have been removed'
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <h3>Total : {updatedProducts.reduce((prev, curr) => prev + ( curr.unitPrice * curr.quantity ), 0)} AED</h3>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2
              }}
            >
              <Button
                disabled={updatedProducts.every(updatedProduct => updatedProduct.quantity === 0)}
                color="primary"
                variant="contained"
                onClick={saveRefundProducts}
              >
                Refund Products
              </Button>
              <Button
                color="primary"
                onClick={props.closeModal.bind(this, false)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  )
};

export default connect (mapStateToProps, {setNewSOProducts, setNewSOPaymentInfo, refundSalesOrder}) (RefundProductsModal);